import { useEffect, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, transform, useMotionValue, useReducedMotion, useScroll, useTransform, type MotionValue } from 'motion/react';
import ProjectIcon from '../../ProjectIcon/ProjectIcon';
import Icon from '../../ui/Icon/Icon';
import PillLink from '../../ui/PillLink/PillLink';
import { plainProjectName, primaryLink } from '../../../data/projects';
import type { Showcase } from '../../../data/showcase';
import { useHasMounted } from '../../../hooks/useHasMounted';
import { useProjectCopy } from '../../../hooks/useProjectCopy';
import { useLanguage } from '../../../i18n/useLanguage';
import styles from './ProjectStage.module.css';

interface ProjectStageProps {
    showcase: Showcase;
    headingId: string;
}

interface StageMotion {
    introY?: MotionValue<number>;
    hintOpacity?: MotionValue<number>;
    mediaOpacity?: MotionValue<number>;
    mediaY?: MotionValue<string>;
    infoOpacity?: MotionValue<number>;
    infoY?: MotionValue<number>;
}

interface StageLayoutProps extends ProjectStageProps {
    animated: boolean;
    typedCount: number;
    trackRef?: RefObject<HTMLDivElement | null>;
    stickyRef?: RefObject<HTMLDivElement | null>;
    introRef?: RefObject<HTMLDivElement | null>;
    stageMotion: StageMotion;
}

const TALL_QUERY = '(max-width: 47.9375rem)';
const HINT_RANGE = [0.06, 0.14];
const TYPING_RANGE = [0.12, 0.42];
const INTRO_RANGE = [0.44, 0.6];
const MEDIA_RANGE = [0.5, 0.74];
const INFO_RANGE = [0.72, 0.88];
const INFO_OFFSET = 40;
const MEDIA_OFFSET = ['24%', '0%'];
const INTRO_START_SHARE = 0.4;

function useIntroEntrance(stickyRef: RefObject<HTMLDivElement | null>, introRef: RefObject<HTMLDivElement | null>) {
    const y = useMotionValue(0);

    useEffect(() => {
        const measure = () => {
            const sticky = stickyRef.current;
            const intro = introRef.current;
            if (!sticky || !intro) return;
            y.set(sticky.clientHeight * INTRO_START_SHARE - (intro.offsetTop + intro.offsetHeight / 2));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [stickyRef, introRef, y]);

    return y;
}

function StageLayout({ showcase, headingId, animated, typedCount, trackRef, stickyRef, introRef, stageMotion }: StageLayoutProps) {
    const { t } = useTranslation();
    const { localizedPath } = useLanguage();
    const { project } = showcase;
    const copy = useProjectCopy()(project.slug);
    const name = plainProjectName(project);
    const detailPath = localizedPath(`/work/${project.slug}`);
    const productLink = primaryLink(project);
    const brief = t('home.work.brief');
    const typedBrief = brief.slice(0, typedCount);
    const briefComplete = typedCount >= brief.length;

    return (
        <div ref={trackRef} className={styles.track} data-animated={animated}>
            <div ref={stickyRef} className={styles.sticky}>
                <motion.div ref={introRef} className={styles.intro} style={{ y: stageMotion.introY }}>
                    <h2 id={headingId} className={styles.lockup}>
                        <Link to={detailPath} className={styles.lockupLink}>
                            <ProjectIcon project={project} size="large" />
                            <span>{project.name}</span>
                        </Link>
                    </h2>
                    <p className={styles.summary}>{copy.summary}</p>
                    <div className={styles.composer} data-complete={briefComplete}>
                        <p className={styles.brief} aria-label={brief}>
                            <span aria-hidden="true">{typedBrief}</span>
                            <span className={styles.caret} aria-hidden="true" />
                        </p>
                        <div className={styles.composerRow}>
                            <span className={styles.briefHint}>{t('home.work.briefHint')}</span>
                            {productLink && <PillLink href={productLink.href} icon="arrowUpRight">{t('home.work.briefAction')}</PillLink>}
                        </div>
                    </div>
                    <motion.p className={styles.scrollHint} style={{ opacity: stageMotion.hintOpacity }} aria-hidden="true">
                        <span className={styles.scrollIcon}><Icon name="arrowDown" /></span>
                        {t('home.work.scrollHint')}
                    </motion.p>
                </motion.div>
                <motion.figure className={styles.figure} style={{ opacity: stageMotion.mediaOpacity, y: stageMotion.mediaY }}>
                    <Link to={detailPath} className={styles.media} aria-label={t('project.open', { name })}>
                        <picture>
                            <source media={TALL_QUERY} srcSet={showcase.tallImage} />
                            <img src={showcase.wideImage} alt={t('home.work.showcaseAlt')} width={showcase.width} height={showcase.height} loading="lazy" decoding="async" className={styles.image} />
                        </picture>
                    </Link>
                    <figcaption className={styles.caption}>{t('home.work.showcaseCaption')}</figcaption>
                </motion.figure>
                <motion.div className={styles.info} style={{ opacity: stageMotion.infoOpacity, y: stageMotion.infoY }}>
                    <span className={styles.status}>{t(`status.${project.status}`)}</span>
                    <PillLink href={detailPath} variant="outline" icon="arrowRight">{t('project.open', { name })}</PillLink>
                </motion.div>
            </div>
        </div>
    );
}

function ScrollingStage(props: ProjectStageProps) {
    const { t } = useTranslation();
    const briefLength = t('home.work.brief').length;
    const trackRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);
    const introRef = useRef<HTMLDivElement>(null);
    const [typedCount, setTypedCount] = useState(0);
    const entranceY = useIntroEntrance(stickyRef, introRef);
    const { scrollYProgress } = useScroll({ target: trackRef, offset: ['start start', 'end end'] });

    useEffect(() => scrollYProgress.on('change', (progress) => {
        setTypedCount(Math.round(transform(progress, TYPING_RANGE, [0, briefLength])));
    }), [scrollYProgress, briefLength]);

    const stageMotion: StageMotion = {
        introY: useTransform([scrollYProgress, entranceY], ([progress, y]: number[]) => transform(progress, INTRO_RANGE, [y, 0])),
        hintOpacity: useTransform(scrollYProgress, HINT_RANGE, [1, 0]),
        mediaOpacity: useTransform(scrollYProgress, MEDIA_RANGE, [0, 1]),
        mediaY: useTransform(scrollYProgress, MEDIA_RANGE, MEDIA_OFFSET),
        infoOpacity: useTransform(scrollYProgress, INFO_RANGE, [0, 1]),
        infoY: useTransform(scrollYProgress, INFO_RANGE, [INFO_OFFSET, 0])
    };

    return <StageLayout {...props} animated typedCount={typedCount} trackRef={trackRef} stickyRef={stickyRef} introRef={introRef} stageMotion={stageMotion} />;
}

export default function ProjectStage(props: ProjectStageProps) {
    const { t } = useTranslation();
    const hasMounted = useHasMounted();
    const prefersReducedMotion = useReducedMotion();

    if (!hasMounted || prefersReducedMotion) {
        return <StageLayout {...props} animated={false} typedCount={t('home.work.brief').length} stageMotion={{}} />;
    }

    return <ScrollingStage {...props} />;
}
